import { useState } from "react";
import {
  IconArrowLeft,
  IconEye,
  IconEyeOff,
  IconLock,
  IconMail,
  IconShieldLock,
} from "@tabler/icons-react";
import { getIn, useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuth } from "@context/AuthContext";
import InlineError from "@components/forms/InlineError";
import useLogin from "@hooks/auth/useLogin";
import authBackgroundImage from "@assets/img/auth/bg-auth.webp";
import brandLogo from "@assets/img/brand/logo.png";

const loginValidationSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Enter a valid email.")
    .required("Email is required."),
  password: yup
    .string()
    .required("Password is required.")
    .min(8, "Password must be at least 8 characters.")
    .matches(/[A-Z]/, "Password must include at least 1 uppercase letter.")
    .matches(/\d/, "Password must include at least 1 number.")
    .matches(
      /[^A-Za-z0-9]/,
      "Password must include at least 1 special character.",
    ),
});

const verifyCodeValidationSchema = yup.object({
  code: yup
    .string()
    .trim()
    .matches(/^\d{6}$/, "Security code must contain 6 digits.")
    .required("Security code is required."),
});

function LoginPage() {
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useAuth();
  const {
    executeLogin,
    executeVerifyLoginCode,
    isSubmitting,
    error,
    setError,
    challenge,
    resetChallenge,
  } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const loginFormik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginValidationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setError("");
      try {
        await executeLogin(values);
      } catch (submitError) {
        setError(submitError?.message || "Could not sign in.");
      }
    },
  });
  const codeFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: "",
    },
    validationSchema: verifyCodeValidationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setError("");
      try {
        const response = await executeVerifyLoginCode({
          challengeToken: String(challenge?.challengeToken || ""),
          code: values.code,
        });
        setAuthenticatedUser(response?.body || null);
        navigate("/cms");
      } catch (submitError) {
        setError(submitError?.message || "Could not verify security code.");
      }
    },
  });
  const isVerifyStep = Boolean(challenge?.challengeToken);

  function getFieldError(path) {
    const activeFormik = isVerifyStep ? codeFormik : loginFormik;
    const touched = getIn(activeFormik.touched, path);
    const fieldError = getIn(activeFormik.errors, path);
    return touched ? fieldError || "" : "";
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isVerifyStep) {
      codeFormik.setTouched(
        {
          code: true,
        },
        true,
      );
    } else {
      loginFormik.setTouched(
        {
          email: true,
          password: true,
        },
        true,
      );
    }
    setError("");
    await (isVerifyStep ? codeFormik.submitForm() : loginFormik.submitForm());
  }

  function handleBackToCredentials() {
    resetChallenge();
    codeFormik.resetForm();
    setError("");
  }

  return (
    <main
      className="auth-page auth-page--login"
      style={{ backgroundImage: `url(${authBackgroundImage})` }}
    >
      <section className="auth-card">
        <div className="auth-brand">
          <img className="auth-brand-logo" src={brandLogo} alt="Exploor CMS" />
        </div>
        <h1 className="auth-title">
          {isVerifyStep ? "Verify Security Code" : "Sign In"}
        </h1>
        <p className="auth-subtitle">
          {isVerifyStep
            ? `Enter the 6-digit code sent to ${challenge?.email || "your email"}.`
            : "Access your CMS panel with email and password."}
        </p>
        <form className="form-grid auth-form-grid" onSubmit={handleSubmit}>
          {isVerifyStep ? (
            <>
              <label className="field-label" htmlFor="code">
                <span className="auth-field-label">
                  Security Code
                  {getFieldError("code") ? (
                    <InlineError>{getFieldError("code")}</InlineError>
                  ) : null}
                </span>
                <span className="field-label app-form-field auth-form-field">
                  <span className="auth-input-icon" aria-hidden="true">
                    <IconShieldLock size={18} />
                  </span>
                  <input
                    className="field-input"
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={codeFormik.values.code}
                    onChange={(event) => {
                      const normalizedValue = String(event.target.value || "")
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      codeFormik.setFieldValue("code", normalizedValue);
                    }}
                    onBlur={codeFormik.handleBlur}
                    placeholder="Enter the 6-digit code"
                  />
                </span>
              </label>
              <button
                className="button-primary auth-submit-btn"
                type="submit"
                disabled={isSubmitting || !codeFormik.isValid}
              >
                {isSubmitting ? "Verifying..." : "Verify code"}
              </button>
              <button
                type="button"
                className="app-icon-btn app-icon-btn--back"
                onClick={handleBackToCredentials}
                aria-label="Go back"
                title="Go back"
              >
                <IconArrowLeft size={18} />
              </button>
            </>
          ) : (
            <>
              <label className="field-label" htmlFor="email">
                <span className="auth-field-label">
                  Email
                  {getFieldError("email") ? (
                    <InlineError>{getFieldError("email")}</InlineError>
                  ) : null}
                </span>
                <span className="field-label app-form-field auth-form-field">
                  <span className="auth-input-icon" aria-hidden="true">
                    <IconMail size={18} />
                  </span>
                  <input
                    className="field-input"
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={loginFormik.values.email}
                    onChange={loginFormik.handleChange}
                    onBlur={loginFormik.handleBlur}
                    placeholder="Enter your email"
                  />
                </span>
              </label>
              <label className="field-label" htmlFor="password">
                <span className="auth-field-label">
                  Password
                  {getFieldError("password") ? (
                    <InlineError>{getFieldError("password")}</InlineError>
                  ) : null}
                </span>
                <span className="field-label app-form-field auth-form-field">
                  <span className="auth-input-icon" aria-hidden="true">
                    <IconLock size={18} />
                  </span>
                  <input
                    className="field-input auth-password-input"
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={loginFormik.values.password}
                    onChange={loginFormik.handleChange}
                    onBlur={loginFormik.handleBlur}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <IconEyeOff size={18} />
                    ) : (
                      <IconEye size={18} />
                    )}
                  </button>
                </span>
              </label>
              <button
                className="button-primary auth-submit-btn"
                type="submit"
                disabled={isSubmitting || !loginFormik.isValid}
              >
                {isSubmitting ? "Sending code..." : "Sign in"}
              </button>
            </>
          )}
          {error ? <p className="message-error">{error}</p> : null}
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
